/**
 * The printable-form settings model: which forms exist, the two paper sizes, the bounds on
 * every number, and the parser that turns raw form-field strings into a settings object.
 *
 * Everything the generator draws is derived from a `FormSettings` that passed `parseSettings`,
 * so no out-of-range value can reach the layout. The bounds are chosen so that every legal
 * combination fits on both papers — the layout still checks, and reports rather than clips,
 * for the parts that depend on text (title width, checklist items).
 *
 * Pure: no DOM, no font, no pdf-lib. Unit-tested directly.
 */

export const FORMS = ['lined', 'grid', 'weekly', 'checklist'] as const;
export type FormKind = (typeof FORMS)[number];

export function isFormKind(value: unknown): value is FormKind {
  return typeof value === 'string' && (FORMS as readonly string[]).includes(value);
}

/** PDF points per millimetre. */
export const PT_PER_MM = 72 / 25.4;

export const mmToPt = (mm: number): number => mm * PT_PER_MM;

/**
 * ISO 216 A4 is 210 × 297 mm; ANSI US Letter is 8.5 × 11 in. Points are 1/72 in, rounded to
 * 2 decimals as every PDF producer writes them.
 */
export const PAPERS = {
  a4: { width: 595.28, height: 841.89 },
  letter: { width: 612, height: 792 },
} as const;
export type PaperKind = keyof typeof PAPERS;
export const PAPER_KINDS = Object.keys(PAPERS) as PaperKind[];

export const WEEK_STARTS = ['monday', 'sunday'] as const;
export type WeekStart = (typeof WEEK_STARTS)[number];

export interface NumberBounds {
  min: number;
  max: number;
  default: number;
  step: number;
}

export const MARGIN_MM: NumberBounds = { min: 10, max: 30, default: 15, step: 1 };

/** Line pitch for lined paper, cell size for graph paper, row height for checklists. */
export const SPACING_MM: Record<Exclude<FormKind, 'weekly'>, NumberBounds> = {
  lined: { min: 5, max: 15, default: 8, step: 0.5 },
  grid: { min: 3, max: 15, default: 5, step: 0.5 },
  checklist: { min: 7, max: 20, default: 10, step: 0.5 },
};

export function hasSpacing(form: FormKind): form is Exclude<FormKind, 'weekly'> {
  return form !== 'weekly';
}

/** Counted in code points after normalisation, so a Hangul syllable counts once. */
export const TITLE_MAX_CHARS = 60;
export const CHECKLIST_MAX_ITEMS = 30;
export const CHECKLIST_ITEM_MAX_CHARS = 80;

export interface FormSettings {
  form: FormKind;
  paper: PaperKind;
  /** Normalised; '' means "no title". */
  title: string;
  marginMm: number;
  /** Unused for the weekly planner. */
  spacingMm: number;
  /** Weekly planner only. */
  weekStart: WeekStart;
  /** Checklist only: pre-printed items, already trimmed and non-empty. */
  items: string[];
}

/** What the form fields hold, as strings, before validation. */
export interface RawSettings {
  paper: string;
  title: string;
  margin: string;
  spacing: string;
  weekStart: string;
  items: string;
}

export type SettingsField = 'paper' | 'title' | 'margin' | 'spacing' | 'weekStart' | 'items';

export type IssueCode =
  | 'required'
  | 'notNumber'
  | 'outOfRange'
  | 'titleTooLong'
  | 'itemTooLong'
  | 'tooManyItems'
  | 'invalidChoice'
  // Raised by the layout, which is the part that knows the font and the page:
  | 'unsupportedChars'
  | 'titleTooWide'
  | 'itemTooWide'
  | 'itemsDoNotFit'
  | 'noRoom';

export interface Issue {
  field: SettingsField;
  code: IssueCode;
  min?: number;
  max?: number;
  /** 1-based checklist line the issue is about. */
  line?: number;
  /** Characters the font cannot draw, deduplicated. */
  chars?: string[];
}

export function defaultRaw(form: FormKind): RawSettings {
  return {
    paper: 'a4',
    title: '',
    margin: String(MARGIN_MM.default),
    spacing: hasSpacing(form) ? String(SPACING_MM[form].default) : '',
    weekStart: 'monday',
    items: '',
  };
}

/**
 * Canonical text: NFC (so a syllable typed as separate jamo becomes one character and is
 * found in the font), control characters dropped, runs of whitespace folded to one space.
 */
export function normalizeText(value: string): string {
  return value
    .normalize('NFC')
    .replace(/\p{Cc}/gu, ' ')
    .replace(/\s+/gu, ' ')
    .trim();
}

export function charCount(value: string): number {
  return Array.from(value).length;
}

/** Accepts "8", "8.5" and the comma decimal "8,5"; nothing else. */
export function parseDecimal(raw: string): number | null | 'empty' {
  const text = raw.trim().replace(',', '.');
  if (text === '') return 'empty';
  if (!/^\d{1,3}(\.\d{1,2})?$/.test(text)) return null;
  const value = Number(text);
  return Number.isFinite(value) ? value : null;
}

function parseBounded(
  raw: string,
  bounds: NumberBounds,
  field: SettingsField,
  issues: Issue[],
): number {
  const parsed = parseDecimal(raw);
  if (parsed === 'empty') {
    issues.push({ field, code: 'required', min: bounds.min, max: bounds.max });
    return bounds.default;
  }
  if (parsed === null) {
    issues.push({ field, code: 'notNumber', min: bounds.min, max: bounds.max });
    return bounds.default;
  }
  if (parsed < bounds.min || parsed > bounds.max) {
    issues.push({ field, code: 'outOfRange', min: bounds.min, max: bounds.max });
    return bounds.default;
  }
  return parsed;
}

export function splitItems(raw: string): string[] {
  return raw
    .split(/\r\n|\r|\n/)
    .map(normalizeText)
    .filter((line) => line !== '');
}

export interface ParseResult {
  /** Always present, so the caller can keep a consistent object; only trust it if `ok`. */
  settings: FormSettings;
  issues: Issue[];
  ok: boolean;
}

export function parseSettings(form: FormKind, raw: RawSettings): ParseResult {
  const issues: Issue[] = [];

  let paper: PaperKind = 'a4';
  if ((PAPER_KINDS as string[]).includes(raw.paper)) paper = raw.paper as PaperKind;
  else issues.push({ field: 'paper', code: 'invalidChoice' });

  const title = normalizeText(raw.title);
  if (charCount(title) > TITLE_MAX_CHARS) {
    issues.push({ field: 'title', code: 'titleTooLong', max: TITLE_MAX_CHARS });
  }

  const marginMm = parseBounded(raw.margin, MARGIN_MM, 'margin', issues);

  const spacingMm = hasSpacing(form)
    ? parseBounded(raw.spacing, SPACING_MM[form], 'spacing', issues)
    : 0;

  let weekStart: WeekStart = 'monday';
  if (form === 'weekly') {
    if ((WEEK_STARTS as readonly string[]).includes(raw.weekStart)) weekStart = raw.weekStart as WeekStart;
    else issues.push({ field: 'weekStart', code: 'invalidChoice' });
  }

  let items: string[] = [];
  if (form === 'checklist') {
    items = splitItems(raw.items);
    if (items.length > CHECKLIST_MAX_ITEMS) {
      issues.push({ field: 'items', code: 'tooManyItems', max: CHECKLIST_MAX_ITEMS });
    }
    items.forEach((item, index) => {
      if (charCount(item) > CHECKLIST_ITEM_MAX_CHARS) {
        issues.push({ field: 'items', code: 'itemTooLong', line: index + 1, max: CHECKLIST_ITEM_MAX_CHARS });
      }
    });
  }

  return {
    settings: { form, paper, title, marginMm, spacingMm, weekStart, items },
    issues,
    ok: issues.length === 0,
  };
}
